import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import LegalFooter from '../components/LegalFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Public landing page reused for /book and /maggiorate.
 *
 * /book        → bookmaker showcase, 3+ cards, ONLY the "consigliato" opens
 *                the real link. The other cards trigger a fake spinner that
 *                spins until tapped again or until a sibling is tapped
 *                (mirrors the rvincente.it/book "fake-load" pattern).
 * /maggiorate  → daily odds boost, ALL cards open the real link (this page
 *                is a price-comparison, not a funneled CTA).
 *
 * Visual: each card is a full-bleed logo (logos paint corner-to-corner with
 * their native background color). The recommended card has an outer ambra
 * glow and a "promo tag" placed BELOW the card.
 */
const LandingPage = ({ pageKey: pageKeyProp }) => {
  const params = useParams();
  const pageKey = pageKeyProp || params.key || 'book';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // For /book only: which non-recommended card is currently showing the
  // fake spinner. `null` = none. Toggling the same id resets to null.
  const [fakeLoadingId, setFakeLoadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/landing/${pageKey}`);
        if (cancelled) return;
        setData(r.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Errore nel caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pageKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <p className="text-red-400 text-sm">{error || 'Contenuto non disponibile'}</p>
        </div>
      </div>
    );
  }

  const books = Array.isArray(data.books) ? data.books : [];
  const showOdds = pageKey === 'maggiorate';

  // Click handler for non-recommended books on /book: spin / toggle / swap.
  const handleFakeClick = (id) => (e) => {
    e.preventDefault();
    setFakeLoadingId(prev => (prev === id ? null : id));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#171717] to-[#0F0F0F] text-white">
      <div className="max-w-md mx-auto px-5 pt-10 pb-4" data-testid={`landing-${pageKey}`}>
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-7" data-testid="landing-brand">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <span className="text-black font-black text-sm tracking-tighter">R</span>
          </div>
          <span className="font-black text-base tracking-[0.25em] text-amber-400">VINCENTE</span>
        </div>

        {/* Headline */}
        <div className="text-center mb-8">
          {data.headline_line1 && (
            <p className="text-gray-300 text-base font-bold uppercase tracking-wide leading-tight" data-testid="landing-headline-line1">
              {data.headline_line1}
            </p>
          )}
          {data.headline_accent && (
            <h1 className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent text-3xl font-black tracking-tight uppercase mt-1 leading-tight" data-testid="landing-headline-accent">
              {data.headline_accent}
            </h1>
          )}
          {data.subtitle && (
            <p className="text-gray-400 text-xs uppercase tracking-[0.2em] mt-4" data-testid="landing-subtitle">
              {data.subtitle}
            </p>
          )}
        </div>

        {/* Book cards */}
        <div className="flex flex-col gap-7" data-testid="landing-books-list">
          {books.map((b) => {
            const isFeatured = b.is_recommended;
            // On /book: non-recommended cards show a fake spinner instead of
            // opening their link. The link is "fake" by design (admin only
            // wants real conversion on the consigliato).
            const isFakeMode = !showOdds && !isFeatured;
            const isThisLoading = isFakeMode && fakeLoadingId === b.id;

            // Outer wrapper styling
            const outerCls = isFeatured
              ? 'border-amber-400/70 shadow-[0_0_38px_-12px_rgba(245,166,35,0.75)]'
              : 'border-[#2D2D2D]';

            // Card height — smaller than before to give the layout more
            // breathing room and match rvincente.it/book proportions.
            const cardHeight = showOdds ? 'h-[78px]' : 'h-[90px]';

            // Inner content
            const CardInner = (
              <div className={`relative ${cardHeight} flex items-stretch rounded-2xl overflow-hidden border-2 ${outerCls} bg-white`}>
                {/* Logo zone — full-bleed, cover */}
                <div className="relative flex-1 h-full">
                  {b.logo_url ? (
                    <img
                      src={b.logo_url}
                      alt={b.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A]">
                      <span className="text-white font-bold text-xl">{b.name}</span>
                    </div>
                  )}
                  {/* Spinner overlay (fake-load on /book non-recommended) */}
                  {isThisLoading && (
                    <div
                      className="absolute inset-0 bg-black/55 backdrop-blur-[1px] flex items-center justify-center"
                      data-testid={`book-spinner-${b.id}`}
                    >
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>

                {/* Odds column (only on /maggiorate) */}
                {showOdds && (
                  <div className={`flex-shrink-0 w-[100px] flex flex-col items-center justify-center ${isFeatured ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-black' : 'bg-[#1A1A1A] text-white'}`}>
                    {b.odds_label && (
                      <span className={`text-[9px] font-black uppercase tracking-wider ${isFeatured ? 'text-black/80' : 'text-gray-400'}`}>
                        {b.odds_label}
                      </span>
                    )}
                    <span className={`font-black leading-none mt-0.5 ${isFeatured ? 'text-2xl' : 'text-xl'}`} data-testid={`book-odds-${b.id}`}>
                      {b.odds_value || '—'}
                    </span>
                  </div>
                )}
              </div>
            );

            // Wrapper layout: card + (optional) inline promo tag. The tag is
            // intentionally kept IN FLOW (not absolute) so that the parent's
            // `space-y-*` rhythm correctly accounts for its height — this
            // prevents the next card from overlapping the tag. We tug it
            // visually upward with `-mt-3` so the top half of the pill
            // overlaps the bottom edge of the card (the "sticker" look).
            return (
              <div key={b.id} data-testid={`book-card-${b.id}`}>
                {isFakeMode ? (
                  <button
                    type="button"
                    onClick={handleFakeClick(b.id)}
                    className="w-full block active:scale-[0.98] transition-transform text-left"
                    aria-busy={isThisLoading}
                    data-testid={`book-fake-btn-${b.id}`}
                  >
                    {CardInner}
                  </button>
                ) : (
                  <a
                    href={b.link || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full block active:scale-[0.98] transition-transform"
                    data-testid={`book-link-${b.id}`}
                  >
                    {CardInner}
                  </a>
                )}
                {isFeatured && b.bonus_tag && (
                  <div className="flex justify-center -mt-3 relative z-10">
                    <span
                      className="inline-block bg-amber-400 text-black px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-[0_4px_14px_-2px_rgba(245,166,35,0.55)]"
                      data-testid={`book-bonus-tag-${b.id}`}
                    >
                      {b.bonus_tag}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer legale ADM */}
        <LegalFooter />
      </div>
      {/* App bottom navigation */}
      <BottomNav />
    </div>
  );
};

export default LandingPage;
